/** Hold one live node per rendered key and update it in place. A rebind is not a teardown. */

import type { Cue } from "@nebulize/sdk";

import type { RenderedDocument, RenderNode } from "./sceneTree";

export interface SceneBackend<Handle> {
  create: (node: RenderNode, parent: Handle | undefined) => Handle;
  update: (handle: Handle, node: RenderNode) => void;
  remove: (handle: Handle) => void;
  /** The OVL-006 seam: play `presentation` on a live node. A backend without one draws no cue. */
  present?: (handle: Handle, presentation: string, cue: Cue) => void;
}

interface Entry<Handle> {
  readonly handle: Handle;
  readonly parentKey: string | undefined;
}

export class SceneMount<Handle> {
  private readonly backend: SceneBackend<Handle>;
  private readonly entries = new Map<string, Entry<Handle>>();

  constructor(backend: SceneBackend<Handle>) {
    this.backend = backend;
  }

  /** Reconcile the live nodes against a freshly built scene. */
  apply(documents: readonly RenderedDocument[]): void {
    const live = new Set<string>();
    for (const document of documents) this.walk(document.root, undefined, live);

    for (const [key, entry] of [...this.entries]) {
      if (live.has(key)) continue;
      this.entries.delete(key);
      this.backend.remove(entry.handle);
    }
  }

  /** The live node a cue presentation attaches to. Rendering the presentation is OVL-006. */
  find(key: string): Handle | undefined {
    return this.entries.get(key)?.handle;
  }

  clear(): void {
    for (const entry of [...this.entries.values()]) this.backend.remove(entry.handle);
    this.entries.clear();
  }

  private walk(node: RenderNode, parentKey: string | undefined, live: Set<string>): void {
    live.add(node.key);
    const existing = this.entries.get(node.key);

    if (existing !== undefined && existing.parentKey === parentKey) {
      this.backend.update(existing.handle, node);
    } else {
      if (existing !== undefined) {
        this.entries.delete(node.key);
        this.backend.remove(existing.handle);
      }
      const parent = parentKey === undefined ? undefined : this.entries.get(parentKey)?.handle;
      this.entries.set(node.key, { handle: this.backend.create(node, parent), parentKey });
    }

    for (const child of node.children) this.walk(child, node.key, live);
  }
}
